<template>
  <div class="wrapper">
    <!-- 店舗選択 -->
    <div class="client-group">
      <div class="flex center gaps">
        <div class="box">
          <h3 class="heading">
            お気に入り異体字管理を行う店舗を選択してください
          </h3>
        </div>
        <div class="box grow" />
        <div class="box top">
          <el-button
            size="small"
            @click="returnHome"
          >
            管理を終了する
          </el-button>
        </div>
      </div>
      <div class="flex center gaps">
        <div class="box">
          店舗選択:
        </div>
        <div class="box">
          <el-select v-model="selectedClientGroupId">
            <el-option
              :value="null"
              label="未選択"
            />
            <el-option
              v-for="clientGroup in clientGroups"
              :key="`clientGroupId-${clientGroup.id}`"
              :label="clientGroup.name"
              :value="clientGroup.id"
            >
              {{ clientGroup.name }}
            </el-option>
          </el-select>
        </div>
        <div class="box grow" />
      </div>
    </div>

    <!-- お気に入り異体字一覧 -->
    <div
      v-if="selectedClientGroupId"
      class="favorite-variant-characters"
    >
      <div class="flex center gaps">
        <div class="box">
          <h3 class="heading">
            お気に入り異体字一覧
          </h3>
        </div>
        <div class="box grow" />
        <div class="box">
          <div class="sub">
            <small>登録数:</small>
            {{ favoriteVariantCharacters.length }}
            <small>件</small>
          </div>
        </div>
      </div>

      <div class="list">
        <!-- ローディング -->
        <div
          v-if="isLoading.favoriteVariantCharacters"
          v-loading="isLoading.favoriteVariantCharacters"
          class="cover"
        />

        <!-- お気に入り異体字一覧のスクローラ -->
        <div class="items">
          <!-- お気に入り異体字登録がなかった場合 -->
          <div
            v-if="!favoriteVariantCharacters.length"
            class="no-item flex center"
          >
            <div class="box">
              登録がありません
            </div>
          </div>

          <!-- お気に入り異体字登録一覧 -->
          <div
            v-for="(item, index) in favoriteVariantCharacters"
            :key="`fvc-${item.id}`"
            class="item"
          >
            <!-- お気に入り異体字: ラベル -->
            <div class="label flex center gaps">
              <div class="box">
                {{ index + 1 }}. {{ fetchFont(item) }}「{{ item.character }}」
              </div>
              <div class="box grow" />
              <div class="box">
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="small"
                  plain
                  circle
                  @click="onDeleteFavoriteVariantCharacter(item)"
                />
                <el-button
                  type="info"
                  icon="el-icon-search"
                  size="small"
                  plain
                  circle
                  @click="onSearchFavoriteVariantCharacter(item)"
                />
              </div>
            </div>

            <!-- お気に入り異体字: 登録中の文字 -->
            <div class="flex center gaps">
              <div class="box character-image">
                <div class="invert">
                  <TabletCharacterImage
                    :char="fetchDefaultCharacter(item)"
                    :font="item.font"
                    :size="80"
                  />
                </div>
              </div>
              <div class="box">
                <font-awesome-icon
                  :icon="['fal', 'chevron-right']"
                  size="2x"
                />
              </div>
              <div class="box character-image">
                <TabletCharacterImage
                  :char="item.variantCharacter"
                  :font="item.font"
                  :size="80"
                />
              </div>
              <div class="box grow" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- お気に入り異体字登録 -->
    <div
      v-if="selectedClientGroupId"
      class="variant-characters"
    >
      <h3 class="heading">
        お気に入り異体字登録
      </h3>
      <div
        v-loading="isLoading.variantCharacterGroup"
        class="registration"
      >
        <!-- お気に入り異体字登録: 検索 -->
        <div class="search">
          <div class="flex center gaps">
            <div class="box">
              書体:
            </div>
            <div class="box">
              <el-select
                v-model="search.font"
                @change="resetSelectedFavoriteVariantCharacter"
              >
                <el-option
                  v-for="font in fontOptions"
                  :key="`font-${font.value}`"
                  :label="font.name"
                  :value="font.value"
                >
                  {{ font.name }}
                </el-option>
              </el-select>
            </div>
            <div class="box">
              文字:
            </div>
            <div class="box">
              <HandwritingInput1Char
                :value="search.character"
                @onChangeInput="onChangeSearchCharacter($event)"
                @onSubmit="onChangeSearchCharacter($event)"
              />
            </div>
            <div class="box grow" />
            <div class="box">
              <el-button
                :type="search.character ? 'primary' : 'info'"
                plain
                :disabled="!search.character"
                @click="onSearchVariantCharacterGroup"
              >
                検 索
              </el-button>
            </div>
          </div>
        </div>

        <!-- お気に入り異体字登録: 異体字候補一覧 -->
        <div class="list">
          <div
            v-if="variantCharacterGroup === undefined"
            class="no-list flex center"
          >
            <div class="box">
              異体字候補がありませんでした
            </div>
          </div>
          <div
            v-if="variantCharacterGroup"
            class="characters"
          >
            <div
              v-for="character in variantCharacterGroup.variantCharacters"
              :key="`character-${character.id}`"
              class="character"
              :class="{
                active: isSelectedFavoriteVariantCharacter(character.id),
              }"
              @click="onSelectVariantCharacter(character)"
            >
              <img :src="character.imageUrlForTablet" />
            </div>
          </div>
        </div>

        <!-- お気に入り異体字登録: 操作 -->
        <div
          v-if="variantCharacterGroup"
          class="actions"
        >
          <div class="flex center gaps">
            <div class="box right">
              <el-button
                type="primary"
                :disabled="!selectedFavoriteVariantCharacter || !selectedFavoriteVariantCharacter.variantCharacterId"
                @click="onUpdateFavoriteVariantCharacter"
              >
                登録する
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiErrorHandler } from "@/services/apiErrorHandler";
import UsecaseFactory from "@/usecases/usecase.factory.js";

// import components
import TabletCharacterImage from "@/components/TabletCharacterImage.vue";
import HandwritingInput1Char from "@/components/HandwritingInput1Char";

const NOTIFY_MESSAGE_UNAVAILABLE_USER = {
  title: "エラーが発生しました",
  message: "有効なユーザではありません",
};
const NOTIFY_DURATION = 5 * 1000;
const FONT_OPTIONS = [
  {
    value: "手彫り風書体",
    name: "手彫り風書体",
  },
  {
    value: "標準書体",
    name: "楷書体",
  },
  {
    value: "合掌体",
    name: "機械書",
  },
  {
    value: "手書き",
    name: "手書き",
  },
  {
    value: "SK書体",
    name: "SK書体",
  },
  {
    value: "機械書手彫風書体",
    name: "機械書(手彫風書体)",
  },
];

export default {
  components: {
    TabletCharacterImage,
    HandwritingInput1Char,
  },
  data () {
    return {
      clientGroups: [],
      selectedClientGroupId: null,
      favoriteVariantCharacters: [],
      isLoading: {
        favoriteVariantCharacters: false,
        variantCharacterGroup: false,
      },
      search: {
        font: "手彫り風書体",
        character: "",
      },
      variantCharacterGroup: null,
      selectedFavoriteVariantCharacter: null,
      loadingView: {},
    };
  },
  computed: {
    fontOptions: function () {
      return FONT_OPTIONS;
    },
  },
  watch: {
    selectedClientGroupId () {
      if (this.selectedClientGroupId) {
        // リセット
        this.favoriteVariantCharacters = [];
        this.variantCharacterGroup = null;
        this.selectedFavoriteVariantCharacter = null;
        this.fetchFavoriteVariantCharacters();
      }
    },
  },
  created () {
  },
  mounted () {
    this.fetchClientGroups();
  },
  methods: {
    /**
     * [returnHome description]
     */
    returnHome () {
      if (!window.confirm("お気に入り異体字管理を終了してもよろしいですか？")) {
        return;
      }

      this.$router.push({
        path: "/home",
      });
    },

    /**
     * [checkUser description]
     * @return  {[type]}  [return description]
     */
    checkUser () {
      const user = this.$store.state.user;

      return !user || !user.sub;
    },

    /**
     * [fetchClientGroups description]
     */
    async fetchClientGroups () {
      const isUnavailableUser = this.checkUser();

      if (isUnavailableUser) {
        this.$notify.error(NOTIFY_MESSAGE_UNAVAILABLE_USER);
        return;
      }

      this.loadingView = this.$loading();

      try {
        const user = await userUsecase.getMe({
          userId: this.$store.state.user.sub,
        });

        this.clientGroups = user.clientGroups;
      } catch (err) {
        apiErrorHandler(this, err);
      } finally {
        this.loadingView.close();
      }
    },

    /**
     * [fetchFavoriteVariantCharacters description]
     */
    async fetchFavoriteVariantCharacters () {
      const clientGroupId = this.selectedClientGroupId;

      this.isLoading.favoriteVariantCharacters = true;

      try {
        const options = { clientGroupId };
        const res = await favoriteVariantCharactersUsecase.getFavoriteVariantCharacters(options);

        this.favoriteVariantCharacters = res;
      } catch (err) {
        apiErrorHandler(this, err);
      } finally {
        this.isLoading.favoriteVariantCharacters = false;
      }
    },

    /**
     * [fetchDefaultCharacter description]
     * @param   {[type]}  item  [item description]
     * @return  {[type]}        [return description]
     */
    fetchDefaultCharacter (item) {
      const char = {
        character: item.character,
        imageUrl: null,
        points: [],
        variantCharacterId: null,
      };

      return char;
    },

    /**
     * [fetchFont description]
     * @param   {[type]}  item  [item description]
     * @return  {[type]}        [return description]
     */
    fetchFont (item) {
      if (item.font === "標準書体") {
        return "楷書体";
      }

      if (item.font === "合掌体") {
        return "機械署";
      }

      if (item.font === "機械書手彫風書体") {
        return "機械書(手彫風書体)";
      }

      return item.font;
    },

    /**
     * [onDeleteFavoriteVariantCharacter description]
     * @param   {[type]}  target  [target description]
     */
    async onDeleteFavoriteVariantCharacter (target) {
      const targetCharacter = `${target.font === "標準書体" ? "楷書体" : target.font}「${target.character}」`;
      if (!window.confirm(`${targetCharacter}をお気に入りから削除してもよろしいですか？`)) {
        return;
      }

      const variantFavoriteCharacterId = target.id;

      this.loadingView = this.$loading();

      try {
        const options = { variantFavoriteCharacterId };
        await favoriteVariantCharactersUsecase.deleteFavoriteVariantCharacter(options);

        this.$notify.success({
          title: `${targetCharacter}をお気に入りから削除しました`,
          duration: NOTIFY_DURATION,
        });
      } catch (err) {
        apiErrorHandler(this, err);
      } finally {
        this.loadingView.close();
        await this.fetchFavoriteVariantCharacters();
        this.onSearchFavoriteVariantCharacter(target);
      }
    },

    /**
     * [onSearchFavoriteVariantCharacter description]
     * @param   {[type]}  target  [target description]
     */
    onSearchFavoriteVariantCharacter (target) {
      this.search.font = target.font;
      this.search.character = target.character;
      this.onSearchVariantCharacterGroup();
    },

    /**
     * [onChangeSearchCharacter description]
     * @param   {[type]}  value  [value description]
     */
    onChangeSearchCharacter (value) {
      // リセット
      this.resetSelectedFavoriteVariantCharacter();

      this.search.character = value;
    },

    /**
     * [resetSelectedFavoriteVariantCharacter description]
     */
    resetSelectedFavoriteVariantCharacter () {
      this.variantCharacterGroup = null;
      this.selectedFavoriteVariantCharacter = null;
    },

    /**
     * [onSearchVariantCharacterGroup description]
     */
    async onSearchVariantCharacterGroup () {
      this.resetSelectedFavoriteVariantCharacter();

      this.isLoading.variantCharacterGroup = true;

      try {
        const q = this.fetchVariantCharacterGroupsQuery();
        const options = { q };
        const variantCharacterGroups = await variantCharacterGroupUsecase.getVariantCharacterGroups(options);

        this.variantCharacterGroup = variantCharacterGroups[0];
        this.selectedFavoriteVariantCharacter = this.fetchSelectedFavoriteVariantCharacter();
      } catch (err) {
        apiErrorHandler(this, err);
      } finally {
        this.isLoading.variantCharacterGroup = false;
      }
    },

    /**
     * [fetchVariantCharacterGroupsQuery description]
     * @return  {[type]}  [return description]
     */
    fetchVariantCharacterGroupsQuery () {
      const {
        font,
        character,
      } = this.search;
      const searchConditions = [character];

      return {
        font,
        searchConditions,
      };
    },

    /**
     * [fetchSelectedFavoriteVariantCharacter description]
     * @return  {[type]}  [return description]
     */
    fetchSelectedFavoriteVariantCharacter () {
      const {
        selectedClientGroupId,
        variantCharacterGroup,
        search,
      } = this;

      if (!variantCharacterGroup) {
        return null;
      }

      const variantFavoriteCharacter = {
        variantCharacterGroupId: variantCharacterGroup.id,
        variantCharacterId: null,
        clientGroupId: selectedClientGroupId,
        ...search,
      };

      // favoriteVariantCharacters からすでにお気に入り登録中の異体字を探す
      const favoriteVariantCharacter = this.favoriteVariantCharacters.find(item => {
        return item.font === search.font && item.character === search.character;
      });

      if (
        favoriteVariantCharacter &&
        favoriteVariantCharacter.variantCharacter
      ) {
        variantFavoriteCharacter.variantCharacterId = favoriteVariantCharacter.variantCharacter.id;
      }

      return variantFavoriteCharacter;
    },

    /**
     * [isSelectedFavoriteVariantCharacter description]
     * @param   {[type]}  characterId  [characterId description]
     * @return  {[type]}               [return description]
     */
    isSelectedFavoriteVariantCharacter (characterId) {
      const { selectedFavoriteVariantCharacter } = this;

      if (
        selectedFavoriteVariantCharacter &&
        !selectedFavoriteVariantCharacter.variantCharacterId
      ) {
        return false;
      } else if (!selectedFavoriteVariantCharacter) {
        return false;
      }

      const { variantCharacterId } = this.selectedFavoriteVariantCharacter;

      return variantCharacterId ? variantCharacterId === characterId : false;
    },

    /**
     * [onSelectVariantCharacter description]
     * @param   {[type]}  character  [character description]
     */
    onSelectVariantCharacter (character) {
      const { selectedFavoriteVariantCharacter } = this;

      const variantFavoriteCharacter = { ...selectedFavoriteVariantCharacter };
      variantFavoriteCharacter.variantCharacterId = character.id;

      this.selectedFavoriteVariantCharacter = variantFavoriteCharacter;
    },

    /**
     * [onUpdateFavoriteVariantCharacter description]
     */
    async onUpdateFavoriteVariantCharacter () {
      const variantFavoriteCharacter = this.selectedFavoriteVariantCharacter;

      this.loadingView = this.$loading();

      try {
        const options = { variantFavoriteCharacter };
        await favoriteVariantCharactersUsecase.updateFavoriteVariantCharacter(options);

        const title = `${variantFavoriteCharacter.font === "標準書体" ? "楷書体" : variantFavoriteCharacter.font}「${variantFavoriteCharacter.character}」をお気に入り登録しました`;
        this.$notify.success({
          title,
          duration: NOTIFY_DURATION,
        });
      } catch (err) {
        apiErrorHandler(this, err);
      } finally {
        this.loadingView.close();
        this.fetchFavoriteVariantCharacters();
      }
    },
  },
};

const userUsecase = UsecaseFactory.create("user");
const variantCharacterGroupUsecase = UsecaseFactory.create("variantCharacterGroup");
const favoriteVariantCharactersUsecase = UsecaseFactory.create("favoriteVariantCharacters");
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/variables.scss";

.wrapper {
  display: grid;
  grid-template-areas:
    "c c"
    "f v";
  grid-template-rows: auto 1fr;
  grid-template-columns: auto 1fr;
  gap: 15px;
  height: calc(100% - 63px);
  width: 100%;
  padding: 15px;
  box-sizing: border-box;

  .heading {
    margin-block-start: 0;
    margin-block-end: 12px;
  }

  .sub {
    margin-bottom: 12px;
  }

  .client-group {
    grid-area: c;
  }

  .favorite-variant-characters {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
    grid-area: f;
    height: 100%;
    position: relative;
    overflow: hidden;

    .list {
      height: 100%;
      width: 360px;
      position: relative;
      border: 1px solid $--border-color-light;
      border-radius: 4px;
      box-sizing: border-box;
      overflow: hidden;

      .cover {
        height: 100%;
        width: 360px;
        position: absolute;
      }

      .items {
        height: 100%;
        width: 360px;
        position: relative;
        overflow-y: scroll;

        .no-item {
          height: 100%;
          width: 100%;
        }

        .item {
          padding: 8px 8px 12px 12px;
          box-sizing: border-box;

          .label {
            margin-bottom: 12px;
            font-size: 18px;
          }

          .character-image {
            padding: 5px;
            border: 1px solid $--border-color-light;

            .invert {
              filter: invert(1);
            }
          }
        }

        .item + .item {
          border-top: 1px dashed $--border-color-light;
        }
      }
    }
  }

  .variant-characters {
    display: grid;
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
    grid-area: v;
    height: 100%;
    position: relative;
    overflow: hidden;

    .registration {
      display: grid;
      grid-template-rows: auto 1fr auto;
      grid-template-columns: 1fr;
      position: relative;
      height: 100%;
      border: 1px solid $--border-color-light;
      border-radius: 4px;
      box-sizing: border-box;
      overflow: hidden;

      .search {
        padding: 12px;
        border-bottom: 1px solid $--border-color-light;
        background-color: $background-gray-light;
      }

      .list {
        height: 100%;
        padding: 12px;
        box-sizing: border-box;
        overflow-y: scroll;

        .no-list {
          height: 100%;
          width: 100%;
        }

        .characters {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;

          .character {
            display: inline-flex;
            height: 108px;
            width: 108px;
            background-color: gray;
            border: 5px solid white;
            box-sizing: content-box;

            &.active {
              border-color: $blue-base;
            }

            img {
              height: 108px;
              width: 108px;
              border: 1px solid white;
              box-sizing: border-box;
            }
          }
        }
      }

      .actions {
        padding: 12px;
        border-top: 1px solid $--border-color-light;
        background-color: white;
      }
    }
  }
}
</style>
